import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Send, Loader2, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';

const leadFormSchema = z.object({
  fullName: z.string().trim().min(2, { message: 'Name must be at least 2 characters' }).max(100),
  workEmail: z.string().trim().email({ message: 'Please enter a valid work email' }).max(255),
  phone: z.string().trim().optional(),
  jobTitle: z.string().optional(),
  crmUsed: z.string().optional(),
});

type LeadFormValues = z.infer<typeof leadFormSchema>;

const CRM_OPTIONS = [
  { value: 'hubspot', label: 'HubSpot' },
  { value: 'rdstation', label: 'RD Station CRM' },
  { value: 'salesforce', label: 'Salesforce' },
  { value: 'pipedrive', label: 'Pipedrive' },
  { value: 'zoho', label: 'Zoho CRM' },
  { value: 'dynamics', label: 'Microsoft Dynamics' },
  { value: 'other', label: 'Other' },
  { value: 'none', label: 'No CRM' },
];

const JOB_TITLE_OPTIONS = [
  'jobTitleCEO',
  'jobTitleVP',
  'jobTitleDirector',
  'jobTitleHead',
  'jobTitleManager',
  'jobTitleRevOps',
  'jobTitleCRO',
  'jobTitleSDR',
  'jobTitleMarketing',
  'jobTitleOther',
] as const;

export function LeadCaptureForm() {
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const form = useForm<LeadFormValues>({
    resolver: zodResolver(leadFormSchema),
    defaultValues: {
      fullName: '',
      workEmail: '',
      phone: '',
      jobTitle: '',
      crmUsed: '',
    },
  });

  async function onSubmit(data: LeadFormValues) {
    setIsSubmitting(true);
    try {
      const { error } = await supabase.from('leads').insert({
        full_name: data.fullName,
        work_email: data.workEmail,
        phone: data.phone || null,
        job_title: data.jobTitle || null,
        crm_used: data.crmUsed || null,
        source: 'landing_page',
      });

      if (error) throw error;

      // Send email notification
      try {
        await supabase.functions.invoke('send-demo-request', {
          body: {
            fullName: data.fullName,
            workEmail: data.workEmail,
            phone: data.phone || '',
            jobTitle: data.jobTitle || '',
            crmUsed: data.crmUsed || '',
          },
        });
      } catch (emailErr) {
        console.error('Email notification failed (lead saved):', emailErr);
      }

      setIsSuccess(true);
      toast({
        title: t('landing.leadForm.successTitle'),
        description: t('landing.leadForm.successMessage'),
      });
    } catch (error) {
      console.error('Error submitting lead:', error);
      toast({
        title: t('landing.leadForm.errorTitle'),
        description: t('landing.leadForm.errorMessage'),
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  if (isSuccess) {
    return (
      <div className="flex flex-col items-center justify-center rounded-2xl border border-border/50 bg-card/50 p-8 text-center backdrop-blur-sm md:p-12">
        <div className="mb-4 rounded-full bg-primary/10 p-4">
          <CheckCircle className="h-12 w-12 text-primary" />
        </div>
        <h3 className="mb-2 text-2xl font-bold text-foreground">
          {t('landing.leadForm.successTitle')}
        </h3>
        <p className="text-muted-foreground">
          {t('landing.leadForm.successMessage')}
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-border/50 bg-card/50 p-6 backdrop-blur-sm md:p-8">
      <div className="mb-6 text-center">
        <h3 className="mb-2 text-xl font-bold text-foreground md:text-2xl">
          {t('landing.leadForm.title')}
        </h3>
        <p className="text-sm text-muted-foreground">
          {t('landing.leadForm.subtitle')}
        </p>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <FormField
            control={form.control}
            name="fullName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('landing.leadForm.name')}</FormLabel>
                <FormControl>
                  <Input
                    placeholder={t('landing.leadForm.namePlaceholder')}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="workEmail"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('landing.leadForm.email')}</FormLabel>
                <FormControl>
                  <Input
                    type="email"
                    placeholder={t('landing.leadForm.emailPlaceholder')}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormItem>
            <FormLabel>{t('landing.leadForm.phone')}</FormLabel>
            <Controller
              name="phone"
              control={form.control}
              render={({ field }) => (
                <PhoneInput
                  international
                  defaultCountry="BR"
                  value={field.value}
                  onChange={(value) => field.onChange(value || '')}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background [&>input]:border-0 [&>input]:bg-transparent [&>input]:outline-none [&>input]:ring-0 [&>input]:text-sm [&>input]:placeholder:text-muted-foreground"
                />
              )}
            />
          </FormItem>

          <FormField
            control={form.control}
            name="jobTitle"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('landing.leadForm.jobTitle')}</FormLabel>
                <Select onValueChange={field.onChange} defaultValue={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder={t('landing.leadForm.jobTitlePlaceholder')} />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {JOB_TITLE_OPTIONS.map((key) => (
                      <SelectItem key={key} value={t(`landing.leadForm.${key}`)}>
                        {t(`landing.leadForm.${key}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="crmUsed"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('landing.leadForm.crm')}</FormLabel>
                <Select onValueChange={field.onChange} defaultValue={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder={t('landing.leadForm.crmPlaceholder')} />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {CRM_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.value === 'other'
                          ? t('landing.leadForm.crmOther')
                          : option.value === 'none'
                          ? t('landing.leadForm.crmNone')
                          : option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <Button
            type="submit"
            className="w-full"
            size="lg"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t('landing.leadForm.submitting')}
              </>
            ) : (
              <>
                {t('landing.leadForm.submit')}
                <Send className="ml-2 h-4 w-4" />
              </>
            )}
          </Button>

          <p className="text-center text-xs text-muted-foreground">
            {t('landing.leadForm.privacy')}
          </p>
        </form>
      </Form>
    </div>
  );
}
